import { Avatar, Box, Typography, styled } from '@mui/material'
import { Link } from 'react-router-dom'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'

import { IBankInstitution } from '@/types'
import { Chip } from '@/components/UI/Chip'
import { Copy } from '@/components/UI/Copy'
import { ITableColumn } from '@/components/UI/DataTable/TableHead'
import { DisplayOrderCell, DisplayOrderEditing } from './DisplayOrderCell'

// Sorted by the service's own presentation rank until an agent chooses
// otherwise.
export const DEFAULT_SORT_BY = 'displayOrder'

// Built per render because the display order cell carries the page's edit state.
export const institutionColumns = (
  editing: DisplayOrderEditing
): ITableColumn<IBankInstitution>[] => [
  {
    key: 'name',
    id: 'name',
    minWidth: '280px',
    label: 'Institution',
    enableSorting: true,
    // The row is mouse-clickable via DataTable's handleRowClick, which gives
    // keyboard and assistive-technology users no way in. The name is a real
    // link so the row is reachable by tab and openable with Enter (and in a
    // new tab), with the row click kept as a mouse convenience.
    render: (value = '', row: IBankInstitution) => (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Avatar sx={{ backgroundColor: '#F6F8F9', width: 28, height: 28 }} src={row.squareLogoUrl}>
          <AccountBalanceOutlinedIcon fontSize="small" sx={{ color: '#344054' }} />
        </Avatar>
        <NameLink to={`/institutions/${row.id}`} onClick={event => event.stopPropagation()}>
          {value}
        </NameLink>
      </Box>
    ),
  },
  {
    key: 'displayOrder',
    id: 'displayOrder',
    minWidth: '140px',
    numeric: true,
    label: 'Display Order',
    enableSorting: true,
    render: (_value: unknown, row: IBankInstitution) => (
      <DisplayOrderCell institution={row} editing={editing} />
    ),
  },
  {
    key: 'status',
    id: 'status',
    minWidth: '180px',
    label: 'Status',
    enableSorting: true,
    render: (value = '') => (value ? <Chip value={String(value)} /> : <StyledText>N/A</StyledText>),
  },
  {
    key: 'id',
    id: 'id',
    minWidth: '320px',
    // Not sortable: ordering opaque UUIDs tells an agent nothing.
    label: 'Institution ID',
    // Copy renders the value itself, so it replaces the text node rather than
    // sitting beside one.
    // Wrapped so the copy click does not bubble to the row handler: copying an
    // ID should stay a copy, not a navigation.
    render: (value = '') =>
      value ? (
        <Box
          onClick={event => event.stopPropagation()}
          sx={{ display: 'inline-flex', alignItems: 'center' }}
        >
          <Copy value={String(value)} textStyle={{ fontSize: '14px', color: '#475467' }} />
        </Box>
      ) : (
        <StyledText>N/A</StyledText>
      ),
  },
]

const NameLink = styled(Link)({
  fontSize: '14px',
  fontFamily: 'Inter',
  color: '#475467',
  wordBreak: 'break-word',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline', color: '#4C4CFF' },
  '&:focus-visible': { outline: '2px solid #4C4CFF', outlineOffset: '2px', borderRadius: '2px' },
})

const StyledText = styled(Typography)({
  fontSize: '14px',
  fontFamily: 'Inter',
  color: '#475467',
  wordBreak: 'break-word',
})
